(ns ui.view.card.counterparty-diligence-screening.tables
  (:require
   [common.util :as cu]
   [common.country :as country]
   [common.localization :as loc]
   [common.schema.entities.counterparty-diligence.templates.questions.common :as cd-questions]
   [reagent.core :as r]
   [re-frame.core :as rf]
   [ui.util :as uu]
   [oops.core :as oops]
   [clojure.string :as string]
   [ui.view.card.counterparty-diligence-screening.model :as model]
   [ui.view.card.counterparty-diligence-screening.sub :as sub]
   [ui.view.card.counterparty-diligence-screening.event :as event]
   [frontend.utils :as fu]
   [ui.components.grid.view :refer [ag-grid-enterprise theme-quartz]]
   [ui.components.radix-ui.tooltip.view :as tooltip]))

(defn country-formatter
  [params]
  (let [value (-> params
                  (js->clj :keywordize-keys true)
                  :value)]
    (when-not (cu/blank-value? value)
      (country/get-label (keyword value)))))

(defn role-in-transaction-label
  [role]
  (->> cd-questions/role-in-transaction
       :options
       (cu/find-first-by :value role)
       :label))

(defn role-in-transaction-formatter
  [params]
  (let [data (-> params
                 (oops/oget "data")
                 (js->clj :keywordize-keys true))
        role (some-> data :role-in-transaction keyword)
        role-label (role-in-transaction-label role)
        other (:role-in-transaction-other data)]
    (or (when (and (= :other role)
                   (not (cu/blank-value? other)))
          other)
        role-label
        "")))

(defn role-in-transaction-column
  []
  {:field "role-in-transaction"
   :width 185
   :headerName (loc/tr :role-in-transaction)
   :cellDataType :text
   :valueFormatter role-in-transaction-formatter})

(defn boolean-formatter
  [params]
  (let [value (-> params
                  (js->clj :keywordize-keys true)
                  :value)]
    (if value
      (loc/tr :true)
      (loc/tr :false))))

(defn sanction-status-badge
  [{:keys [sanction-status entity-type]}]
  (let [entity-type-label-lc (-> entity-type
                                 loc/tr
                                 string/lower-case)]
    [tooltip/view
     [:div
      {:class (uu/cm "rounded px-3 py-1 flex items-center gap-2"
                     (case sanction-status
                       :sanction-status/green "bg-primary-regular"
                       :sanction-status/yellow "bg-warning-regular"
                       :sanction-status/red "bg-danger-regular"
                       ""))}

      [:i {:class (uu/cm (case sanction-status
                           :sanction-status/green "fal fa-check-circle"
                           :sanction-status/yellow "fal fa-exclamation-triangle"
                           :sanction-status/red "fal fa-exclamation-circle text-danger-regular-text font-medium"
                           ""))}]
      [:p
       {:class (uu/cm "leading-tight"
                      (case sanction-status
                        :sanction-status/red
                        "text-danger-regular-text"
                        ""))}
       (case sanction-status
         :sanction-status/green
         (loc/tr :sanction-status-green-label)

         :sanction-status/yellow
         (loc/tr :sanction-status-yellow-label)

         :sanction-status/red
         (loc/tr :sanction-status-red-label)

         nil)]]

     [:span
      (case sanction-status
        :sanction-status/green
        (loc/tr :sanction-status-green-info [entity-type-label-lc])

        :sanction-status/yellow
        (loc/tr :sanction-status-yellow-info [entity-type-label-lc])

        :sanction-status/red
        (loc/tr :sanction-status-red-info [entity-type-label-lc])

        nil)]]))

(defn sanction-status-column
  [{:keys [entity-type no-screening-data-col-span]}]
  {:field "screening-profile.sanction-status"
   :width 150
   :headerName (loc/tr :sanction-status)
   :colSpan (fn [params]
              (let [data (-> params
                             (oops/oget "data")
                             (js->clj :keywordize-keys true))
                    screening-profile (:screening-profile data)]
                (if screening-profile
                  1
                  no-screening-data-col-span)))
   :cellRenderer (fn [params]
                   (let [value (oops/ocall params "getValue")
                         sanction-status (case value
                                           "green" :sanction-status/green
                                           "yellow" :sanction-status/yellow
                                           "red" :sanction-status/red
                                           nil)]
                     (r/as-element
                      [:div
                       {:class (uu/cm "w-full h-full flex items-center justify-start")}
                       (if sanction-status
                         [sanction-status-badge
                          {:sanction-status sanction-status
                           :entity-type entity-type}]
                         [:p
                          {:class "text-base-light-text-paler"}
                          (loc/tr :no-screening-data-found)])])))})

(defn risk-screen-link-column
  []
  {:field :riskscreen-link
   :headerName (loc/tr :riskscreen-link)
   :cellRenderer (fn [params]
                   (let [data (-> params
                                  (oops/oget "data")
                                  (js->clj :keywordize-keys true))
                         external-screening-id (-> data
                                                   :screening-profile
                                                   :external-screening-id)]
                     (r/as-element
                      [:div
                       (when external-screening-id
                         [:a
                          {:href   (model/entity-risk-screen-url external-screening-id)
                           :target "_blank"}
                          (loc/tr :view-in-riskscreen)])])))})

(defn legal-entity-key-persons-columns
  [{:keys [date-format]}]
  [{:field :first-name
    :headerName (loc/tr :first-name)}
   {:field :last-name
    :width 150
    :headerName (loc/tr :last-name)}
   {:field :date-of-birth
    :headerName (loc/tr :date-of-birth)
    :width 150
    :valueFormatter (fn [params]
                      (let [value (-> params
                                      (js->clj :keywordize-keys true)
                                      :value)]
                        (uu/format-date value date-format)))}
   {:field :country
    :headerName (loc/tr :country)
    :width 150
    :valueFormatter country-formatter}
   {:field :role
    :headerName (loc/tr :role)
    :width 150
    :valueFormatter (fn [params]
                      (let [data (-> params
                                     (oops/oget "data")
                                     (js->clj :keywordize-keys true))

                            values (cond (-> data
                                             :roles
                                             seq)
                                         (->> data
                                              :roles
                                              (mapv keyword))

                                         (:role data)
                                         [(-> data
                                              :role
                                              keyword)])
                            value-str (when values
                                        (->> values
                                             (mapv loc/tr)
                                             (string/join "; ")))]
                        value-str))}
   {:field :is-pep?
    :headerName "PEP?"
    :width 100
    :valueFormatter (fn [params]
                      (let [value (oops/oget params "value")]
                        (loc/tr (if value :true :false))))}

   (sanction-status-column
    {:entity-type :person
     :no-screening-data-col-span 5})
   {:field "screening-profile.pep-status"
    :width 120
    :headerName (loc/tr :screening-profile/pep-status)
    :cellDataType :text
    :valueFormatter boolean-formatter}
   {:field "screening-profile.rca-status"
    :width 120
    :headerName (loc/tr :screening-profile/rca-status)
    :cellDataType :text
    :valueFormatter boolean-formatter}
   (risk-screen-link-column)])

(defn legal-entity-key-persons-table
  [{:keys [key-persons row-selection? date-format]}]
  (let [row-data key-persons]
    [:div
     {:style {:height "auto"}}
     [:div
      {:class "h-full w-full"}
      [ag-grid-enterprise
       (merge
        (when row-selection?
          {:rowSelection {:mode "multiRow"}})
        {:theme theme-quartz
         :columnDefs (legal-entity-key-persons-columns
                      {:date-format date-format})
         :rowData row-data
         :suppressContextMenu true
         :defaultColDef {:sortable true
                         :suppressHeaderMenuButton true
                         :suppressColumnsToolPanel true
                         :suppressFiltersToolPanel true
                         :suppressHeaderContextMenu true
                         :autoSize true
                         :resizable true
                         :suppressMovable true
                         :menuTabs []
                         :cellClass "overflow-hidden"}
         :getRowId (fn [params]
                     (-> params
                         (oops/oget "data")
                         (oops/oget "_id")))
         :onRowClicked (fn [_params])
         :suppressRowHoverHighlight true
         :groupDefaultExpanded 0
         :suppressMultiSort true
         :groupDisplayType :groupRows
         :domLayout :autoHeight
         :suppressFiltersToolPanel true})]]]))

(defn natural-persons-table
  [{:keys [ctx]}]
  (let [initially-selected-row-ids @(rf/subscribe [::sub/selected-natural-persons ctx])]
    (fn [{:keys [ctx natural-persons row-selection? date-format]}]
      (let [row-data natural-persons]
        [:div
         {:style {:height "auto"}}
         [:div
          {:class "h-full w-full"}
          [ag-grid-enterprise
           (merge
            (when row-selection?
              {:rowSelection {:mode :multiRow}})
            {:theme theme-quartz
             :onGridReady (fn [params]
                            (let [api (oops/oget params "api")]
                              (doseq [row-id initially-selected-row-ids]
                                (let [node (oops/ocall api "getRowNode" row-id)]
                                  (oops/ocall node "setSelected" true)))))
             :onRowSelected (fn [params]
                              (let [node (oops/oget params "node")
                                    row-id (oops/oget node "id")
                                    selected? (oops/ocall node "isSelected")]
                                (rf/dispatch [::event/set-natural-person-selected
                                              ctx
                                              {:row-id row-id
                                               :selected? selected?}])))
             :rowData row-data
             :columnDefs [{:field :first-name
                           :headerName (loc/tr :first-name)}
                          {:field :last-name
                           :width 150
                           :headerName (loc/tr :last-name)}
                          {:field :date-of-birth
                           :headerName (loc/tr :date-of-birth)
                           :width 150
                           :valueFormatter (fn [params]
                                             (let [value (-> params
                                                             (js->clj :keywordize-keys true)
                                                             :value)]
                                               (uu/format-date value date-format)))}
                          {:field :country
                           :headerName (loc/tr :country)
                           :width 150
                           :valueFormatter country-formatter}
                          (role-in-transaction-column)
                          (sanction-status-column
                           {:entity-type :person
                            :no-screening-data-col-span 4})
                          {:field "screening-profile.pep-status"
                           :width 150
                           :headerName (loc/tr :screening-profile/pep-status)
                           :cellDataType :text
                           :valueFormatter boolean-formatter}
                          {:field "screening-profile.rca-status"
                           :width 150
                           :headerName (loc/tr :screening-profile/rca-status)
                           :cellDataType :text
                           :valueFormatter boolean-formatter}
                          (risk-screen-link-column)]

             :suppressContextMenu true
             :defaultColDef {:sortable true
                             :suppressHeaderMenuButton true
                             :suppressColumnsToolPanel true
                             :suppressFiltersToolPanel true
                             :autoSize true
                             :resizable true
                             :suppressMovable true
                             :menuTabs []
                             :cellClass "overflow-hidden"}
             :getRowId (fn [params]
                         (-> params
                             (oops/oget "data")
                             (oops/oget "_id")))
             :suppressRowHoverHighlight true
             :groupDefaultExpanded 0
             :suppressMultiSort true
             :groupDisplayType :groupRows
             :domLayout :autoHeight
             :suppressFiltersToolPanel true})]]]))))

(defn legal-entities-table
  [{:keys [ctx] :as _props}]
  (let [initially-selected-row-ids @(rf/subscribe [::sub/selected-legal-entities ctx])
        initially-expanded-rows @(rf/subscribe [::sub/expanded-table-rows ctx])]
    (fn [{:keys [legal-entities expandable? row-selection? date-format ctx]}]
      (let [row-data legal-entities]
        [:div
         {:style {:height "auto"}}
         [:div
          {:class "h-full w-full sadsdff"}
          [ag-grid-enterprise
           (merge
            (when row-selection?
              {:rowSelection {:mode "multiRow"}})
            {:theme theme-quartz
             :masterDetail expandable?
             :embedFullWidthRows true
             :detailRowAutoHeight true
             :getRowId (fn [params]
                         (-> params
                             (oops/oget "data")
                             (oops/oget "_id")))
             :onRowSelected (fn [params]
                              (let [node (oops/oget params "node")
                                    data (-> node
                                             (oops/oget "data")
                                             (js->clj :keywordize-keys true))
                                    selected? (oops/ocall node "isSelected")
                                    row-id (oops/oget node "id")]
                                (rf/dispatch [::event/set-legal-entity-selected
                                              ctx
                                              {:row-id row-id
                                               :selected? selected?
                                               :row-data data}])))
             :onRowGroupOpened (fn [params]
                                 (let [node (oops/oget params "node")
                                       row-id (oops/oget node "id")
                                       row-data (-> node
                                                    (oops/oget "data")
                                                    (js->clj :keywordize-keys true))
                                       expanded? (oops/oget params "expanded")]
                                   (rf/dispatch [::event/toggle-expand-table-row
                                                 ctx
                                                 {:row-id row-id
                                                  :row-data row-data
                                                  :expanded? expanded?}])))
             :onGridReady (fn [params]
                            (let [api (oops/oget params "api")]
                              (doseq [row-id initially-selected-row-ids]
                                (let [node (oops/ocall api "getRowNode" row-id)]
                                  (oops/ocall node "setSelected" true)))
                              (doseq [row-id initially-expanded-rows]
                                (let [node (oops/ocall api "getRowNode" row-id)]
                                  (oops/ocall api "setRowNodeExpanded" node true)))))

             :columnDefs [(merge
                           {:field :company-name
                            :headerName (loc/tr :company-name)
                            :flex 1
                            :minWidth 150}
                           (when expandable?
                             {:cellRenderer "agGroupCellRenderer"}))
                          {:field :business-id
                           :width 150
                           :headerName (loc/tr :business-id)}
                          {:field :country
                           :haderName (loc/tr :country)
                           :width 150
                           :valueFormatter country-formatter}
                          (role-in-transaction-column)
                          (sanction-status-column
                           {:entity-type :company
                            :no-screening-data-col-span 2})
                          (risk-screen-link-column)]
             :rowData row-data

             :detailCellRendererParams
             {:detailGridOptions
              {:columnDefs [{:headerName (loc/tr :key-persons)
                             :children (legal-entity-key-persons-columns
                                        {:date-format date-format})}]
               :defaultColDef {:sortable true
                               :suppressHeaderMenuButton true
                               :suppressColumnsToolPanel true
                               :suppressFiltersToolPanel true
                               :autoSize true
                               :resizable true
                               :suppressMovable true
                               :menuTabs []
                               :cellClass "overflow-hidden"}

               :noRowsOverlayComponent (fn []
                                         (r/as-element
                                          [:div (loc/tr :no-key-persons-found)]))}

              :getDetailRowData (fn [params]
                                  (let [data (-> params
                                                 (oops/oget "data")
                                                 (js->clj :keywordize-keys true))
                                        key-persons (:key-persons data)
                                        key-persons  (clj->js key-persons)]
                                    (oops/ocall params "successCallback" key-persons)))}

             :suppressContextMenu true
             :defaultColDef {:sortable true
                             :suppressHeaderMenuButton true
                             :suppressColumnsToolPanel true
                             :suppressFiltersToolPanel true
                             :autoSize true
                             :resizable true
                             :suppressMovable true
                             :menuTabs []}
             :suppressRowHoverHighlight true
             :suppressMultiSort true
             :domLayout :autoHeight
             :suppressFiltersToolPanel true})]]]))))
